import { useState, useEffect } from "react";
import { sanitarywareService } from "../../../services/enhancedCrudService";
import { toast } from "react-toastify";

const SanitarywareTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
  });

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await sanitarywareService.getTypes();
      setTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching types:", error);
      toast.error("Failed to load types");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await sanitarywareService.updateType(editing.id, formData);
        toast.success("Type updated successfully");
      } else {
        await sanitarywareService.createType(formData);
        toast.success("Type created successfully");
      }
      setEditing(null);
      setFormData({ name: "", description: "", status: "active" });
      fetchTypes();
    } catch (error) {
      console.error("Error saving type:", error);
      toast.error("Failed to save type");
    }
  };

  const handleEdit = (type) => {
    setEditing(type);
    setFormData({
      name: type.name,
      description: type.description,
      status: type.status,
    });
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({ name: "", description: "", status: "active" });
  };

  const handleDelete = async (typeId) => {
    if (window.confirm("Are you sure you want to delete this type?")) {
      try {
        await sanitarywareService.deleteType(typeId);
        toast.success("Type deleted successfully");
        fetchTypes();
      } catch (error) {
        console.error("Error deleting type:", error);
        toast.error("Failed to delete type");
      }
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Sanitaryware Types</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-header">
                    <h5>{editing ? "Edit Type" : "Add New Type"}</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label>Type Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          className="form-control"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <button type="submit" className="btn btn-primary">
                          {editing ? "Update Type" : "Add Type"}
                        </button>
                        {editing && (
                          <button
                            type="button"
                            className="btn btn-secondary ml-2"
                            onClick={handleCancel}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <div className="col-md-8">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Products Count</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {types.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No types found
                          </td>
                        </tr>
                      ) : (
                        types.map((type) => (
                          <tr key={type.id}>
                            <td>{type.id}</td>
                            <td>
                              <strong>{type.name}</strong>
                            </td>
                            <td>
                              <div
                                style={{
                                  maxWidth: "200px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {type.description || "No description"}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge badge-${
                                  type.status === "active"
                                    ? "success"
                                    : "danger"
                                }`}
                              >
                                {type.status}
                              </span>
                            </td>
                            <td>
                              <span className="badge badge-info">
                                {type.products_count || 0}
                              </span>
                            </td>
                            <td>
                              {type.created_at
                                ? new Date(type.created_at).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td>
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(type)}
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(type.id)}
                                  title="Delete"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="row mt-4">
                  <div className="col-md-4">
                    <div className="card bg-primary text-white">
                      <div className="card-body text-center">
                        <h4>{types.length}</h4>
                        <p className="mb-0">Total Types</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <h4>
                          {types.filter((t) => t.status === "active").length}
                        </h4>
                        <p className="mb-0">Active Types</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-info text-white">
                      <div className="card-body text-center">
                        <h4>
                          {types.reduce(
                            (total, t) => total + (t.products_count || 0),
                            0
                          )}
                        </h4>
                        <p className="mb-0">Total Products</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanitarywareTypes;
